import { create } from 'zustand';
import { api } from '../utils/api';
import { t } from '../utils/i18n';
import { getBaseClass } from '../utils/classMap';

export const useStore = create((set, get) => ({
  // Auth
  user: null,
  isLoggedIn: false,

  // Character
  character: null,
  party: [],
  setCharacter: (character) => set({ character }),

  // ── Battle State (ownership notes) ──
  // battle      – UI-facing object (read by React + PhaserScene). Always a new ref on update.
  // localBattle – BattleEngine raw state (in-place mutated). ALWAYS shallow-copy on set().
  // coopBattle  – Co-op connection metadata. Set once when entering co-op battle.
  battle: null,
  localBattle: null,
  coopBattle: null,
  setCoopBattle: (data) => set({ coopBattle: data }),

  // Dungeon
  dungeon: null,

  // ── Party Slice (NEW) ──
  partySlice: { members: [], maxSlots: 3, coopParty: null },
  partyDrawerOpen: false,
  togglePartyDrawer: () => set(s => ({ partyDrawerOpen: !s.partyDrawerOpen })),
  openPartyDrawer: () => set({ partyDrawerOpen: true }),
  closePartyDrawer: () => set({ partyDrawerOpen: false }),

  // ── Overlay Slice ──
  // Manages in-game overlay panels (deck, equipment, settings, etc.)
  // rendered on top of Phaser canvas without route changes.
  activeOverlay: null, // 'deck' | 'equipment' | 'settings' | null
  openOverlay: (id) => set({ activeOverlay: id }),
  closeOverlay: () => set({ activeOverlay: null }),

  /** Build unified party member list: slot 0 = player, slot 1+ = mercenaries */
  loadParty: async () => {
    const char = get().character;
    if (!char) return;
    try {
      const mercs = await api.getActiveMercenaries();
      // Only include mercs that are in the party (is_in_party=1).
      // Deactivated mercs remain in the active list (for re-hire UI) but should not appear in party.
      const partyMercs = (mercs || []).filter(m => m.is_in_party === 1 || m.is_in_party === true);
      const members = [
        {
          slot: 0,
          type: 'player',
          id: char.id,
          name: char.name,
          hp: char.hp,
          maxHp: char.max_hp,
          level: char.level,
          classId: char.class_id,
        },
        ...partyMercs.map((m, i) => ({
          slot: i + 1,
          type: 'mercenary',
          id: m.id,
          mercId: m.id,
          name: m.name,
          hp: m.hp,
          maxHp: m.max_hp,
          classId: m.class_id,
        })),
      ];
      set({ partySlice: { ...get().partySlice, members, maxSlots: 3 } });
    } catch {
      // If API fails, at least populate with the player
      if (char) {
        set({
          partySlice: {
            ...get().partySlice,
            members: [{
              slot: 0, type: 'player', id: char.id, name: char.name,
              hp: char.hp, maxHp: char.max_hp, level: char.level, classId: char.class_id,
            }],
          },
        });
      }
    }
  },

  /** Remove a mercenary from party (server-synced).
   *  Optimistic: removes locally first, then confirms via API.
   *  On failure: reverts local state and shows error toast.
   */
  removeMercFromParty: async (mercId) => {
    const prev = get().partySlice;
    // Optimistic local removal
    set({
      partySlice: {
        ...prev,
        members: prev.members.filter(m => m.mercId !== mercId && m.id !== mercId),
      },
    });

    try {
      await api.deactivateMercenary(mercId);
      get().showToast(t('ui.mercRemovedFromParty'));
    } catch (err) {
      console.error('[useStore] removeMercFromParty server error:', err);
      // Rollback: restore previous state
      set({ partySlice: prev });
      get().showToast('⚠️ 용병 제거에 실패했습니다. 다시 시도해주세요.');
    }
  },

  // UI
  loading: false,
  error: null,
  toast: null,

  // Actions
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),
  showToast: (message) => {
    set({ toast: message });
    setTimeout(() => set({ toast: null }), 3000);
  },

  // Auth Actions
  login: async (username, password) => {
    set({ loading: true, error: null });
    try {
      const data = await api.login(username, password);
      set({ user: data.user, isLoggedIn: true, loading: false });
      return true;
    } catch (err) {
      set({ error: err.message, loading: false });
      return false;
    }
  },

  register: async (username, password) => {
    set({ loading: true, error: null });
    try {
      await api.register(username, password);
      set({ loading: false });
      return true;
    } catch (err) {
      set({ error: err.message, loading: false });
      return false;
    }
  },

  checkAuth: async () => {
    if (!api.token) return false;
    try {
      const user = await api.getMe();
      set({ user, isLoggedIn: true });
      return true;
    } catch {
      api.logout();
      return false;
    }
  },

  logout: () => {
    api.logout();
    set({ user: null, isLoggedIn: false, character: null, party: [] });
  },

  // Character Actions
  loadCharacter: async () => {
    try {
      const character = await api.getCharacter();
      const party = await api.getParty();
      set({ character, party: party.partyMembers || [] });
      return character;
    } catch {
      set({ character: null, party: [] });
      return null;
    }
  },

  createCharacter: async (name, classId) => {
    set({ loading: true, error: null });
    try {
      const data = await api.createCharacter(name, classId);
      await api.unlockStarterCards();
      set({ character: data.character, party: data.partyMembers || [], loading: false });
      return true;
    } catch (err) {
      set({ error: err.message, loading: false });
      return false;
    }
  },

  // Battle Actions
  startBattle: async (monsterIds) => {
    set({ loading: true });
    try {
      const battle = await api.startBattle(monsterIds);
      set({ battle, loading: false });
      return battle;
    } catch (err) {
      set({ error: err.message, loading: false });
      return null;
    }
  },

  playCard: async (cardId, targetId) => {
    const { battle } = get();
    if (!battle) return null;

    try {
      const result = await api.playCard(battle.battleId, cardId, targetId);
      set({ battle: { ...battle, ...result } });
      return result;
    } catch (err) {
      get().showToast(err.message);
      return null;
    }
  },

  endTurn: async () => {
    const { battle } = get();
    if (!battle) return null;

    set({ loading: true });
    try {
      const result = await api.endTurn(battle.battleId);
      set({ battle: { ...battle, ...result }, loading: false });
      return result;
    } catch (err) {
      set({ error: err.message, loading: false });
      return null;
    }
  },

  clearBattle: () => set({ battle: null, localBattle: null, coopBattle: null }),

  // PvE 전투 시작 (서버에서 초기 데이터만 받음)
  startLocalBattle: async (monsterTypes, monsterCards, scaling = 1.0) => {
    set({ loading: true });
    try {
      // 서버에서 초기화 데이터 받기
      const initData = await api.initBattle(monsterTypes);
      const BattleEngine = await import('../engine/BattleEngine');

      // 로컬에서 전투 상태 초기화
      const localState = BattleEngine.initBattle(
        {
          hp: initData.player.hp,
          maxHp: initData.player.maxHp,
          atk: initData.player.atk,
          def: initData.player.def,
          deck: initData.deck,
          ap: initData.ap,
        },
        monsterTypes,
        initData.seed,
        monsterCards || initData.monsterCards,
        [],
        initData.mercenaries,
        scaling,
        [],
        initData.monsterTemplates || null
      );

      // battle 형식으로 변환 (UI 호환)
      const battle = convertLocalToBattle(localState, initData);

      set({
        battle,
        localBattle: localState,
        loading: false
      });

      return battle;
    } catch (err) {
      console.error('[startLocalBattle] initBattle failed:', err);
      get().showToast(err?.message || t('ui.battleInitFail'));
      set({ error: null, loading: false });
      return null;
    }
  },

  // 로컬 카드 사용 (서버 통신 없음!)
  playLocalCard: async (cardId, targetIndex = 0) => {
    const { localBattle, battle } = get();
    if (!localBattle) return null;

    const BattleEngine = await import('../engine/BattleEngine');
    const result = BattleEngine.playCard(localBattle, cardId, targetIndex);
    if (result.error) {
      get().showToast(result.error === 'Not enough AP' ? t('ui.notEnoughAP') : result.error);
      return null;
    }

    // UI용 battle 상태 업데이트
    const updatedBattle = convertLocalToBattle(localBattle, battle);
    // Shallow-copy localBattle so zustand detects the change (Engine mutates in-place)
    set({ battle: updatedBattle, localBattle: { ...localBattle } });

    return {
      results: result.results,
      status: localBattle.status,
      logs: result.results,
    };
  },

  // 로컬 턴 종료 (서버 통신 없음!)
  endLocalTurn: async () => {
    const { localBattle, battle } = get();
    if (!localBattle) return null;

    const BattleEngine = await import('../engine/BattleEngine');
    const result = BattleEngine.endTurn(localBattle);

    // UI용 battle 상태 업데이트
    const updatedBattle = convertLocalToBattle(localBattle, battle);
    // Shallow-copy localBattle so zustand detects the change (Engine mutates in-place)
    set({ battle: updatedBattle, localBattle: { ...localBattle } });

    return {
      ...result,
      status: localBattle.status,
      logs: result.results,
    };
  },

  // 전투 결과 제출 (서버 검증 + 보상)
  submitBattleResult: async (opts = {}) => {
    const { localBattle, battle } = get();
    if (!localBattle || (localBattle.status !== 'victory' && localBattle.status !== 'defeat')) return null;

    // Battle submit failures are usually recoverable (retry / reload).
    // Do not lock the whole UI behind GlobalStatus error overlay.
    set({ loading: true, error: null });
    try {
      const BattleEngine = await import('../engine/BattleEngine');
      const battleResult = BattleEngine.generateBattleResult(localBattle);
      const result = await api.submitBattleResult(battle.battleId, battleResult);

      if (result.verified && !opts.silent) {
        if (result.levelUp) {
          get().showToast(`🎉 ${result.levelUp.message}`);
        }
        get().showToast(t('ui.rewardsGain', { gold: result.rewards.gold, xp: result.rewards.xp }));
      } else if (!result.verified && !opts.silent) {
        get().showToast(t('ui.verificationFailed'));
      }

      set({ loading: false, error: null });
      return result;
    } catch (err) {
      console.error('submitBattleResult error', err);
      if (!opts.silent) get().showToast(err?.message || t('ui.requestFailed'));
      set({ loading: false });
      return null;
    }
  },

  claimReward: async () => {
    const { battle } = get();
    if (!battle) return null;

    try {
      const result = await api.claimReward(battle.battleId);
      if (result.levelUp) {
        get().showToast(`🎉 ${result.levelUp.message}`);
      }
      get().showToast(t('ui.rewardsGain', { gold: result.rewards.gold, xp: result.rewards.xp }));
      return result;
    } catch (err) {
      get().showToast(err.message);
      return null;
    }
  },

  // Class Actions
  getClassOptions: async () => {
    try {
      return await api.getClassOptions();
    } catch {
      return null;
    }
  },

  advanceClass: async (targetClassId) => {
    set({ loading: true });
    try {
      const result = await api.advanceClass(targetClassId);
      await get().loadCharacter();
      get().showToast(result.message);
      set({ loading: false });
      return result;
    } catch (err) {
      set({ error: err.message, loading: false });
      return null;
    }
  },

  // Dungeon Actions
  loadDungeon: async () => {
    try {
      const result = await api.getCurrentDungeon();
      set({ dungeon: result.active ? result.run : null });
      return result;
    } catch {
      return null;
    }
  },

  enterDungeon: async (dungeonId, pvpMode) => {
    set({ loading: true });
    try {
      const dungeon = await api.enterDungeon(dungeonId, pvpMode);
      set({ dungeon, loading: false });
      return dungeon;
    } catch (err) {
      set({ error: err.message, loading: false });
      return null;
    }
  },

  nextFloor: async () => {
    try {
      const result = await api.nextFloor();
      if (result.completed) {
        set({ dungeon: null });
        let message = t('ui.dungeonClearRewards', { gold: result.rewards.gold, xp: result.rewards.xp });
        if (result.mercenaryDrop) {
          message += ` ${t('ui.mercenaryDrop', { name: result.mercenaryDrop.name })}`;
        }
        get().showToast(message);
      } else {
        set({ dungeon: { ...get().dungeon, ...result } });
      }
      return result;
    } catch (err) {
      get().showToast(err.message);
      return null;
    }
  },

  // Dungeon Run Continuity: advance floor + auto-start next battle within BattleScene
  // Returns: { completed, event, battle, rewards, ... }
  continueToNextFloor: async () => {
    // Mutex: prevent concurrent calls from advancing multiple floors
    if (get()._floorTransitionInProgress) {
      console.warn('[continueToNextFloor] blocked — transition already in progress');
      return null;
    }
    set({ _floorTransitionInProgress: true });

    try {
      const result = await api.nextFloor();

      if (result.completed) {
        set({ dungeon: null });
        let message = t('ui.dungeonClearRewards', { gold: result.rewards.gold, xp: result.rewards.xp });
        if (result.mercenaryDrop) {
          message += ` ${t('ui.mercenaryDrop', { name: result.mercenaryDrop.name })}`;
        }
        get().showToast(message);
        return { completed: true, rewards: result.rewards };
      }

      // Update dungeon state
      set({ dungeon: { ...get().dungeon, ...result } });

      // Battle or Boss → auto-start next battle (stay in BattleScene)
      if (result.event === 'battle' || result.event === 'boss') {
        try {
          const battleData = await api.startDungeonBattle();
          if (battleData?.startBattle) {
            const battle = await get().startLocalBattle(
              battleData.monsterIds, battleData.monsterCards, battleData.scaling || 1.0
            );
            return { event: result.event, battle, floor: result.currentFloor };
          }
        } catch (e) {
          console.error('[continueToNextFloor] startDungeonBattle failed:', e);
          get().showToast(e.message || t('ui.cannotStartBattle'));
          return { event: 'error', error: e.message };
        }
      }

      // Non-combat events (rest, treasure, trap, empty) → auto-handle + recurse
      if (result.event === 'rest' || result.event === 'treasure' || result.event === 'trap' || result.event === 'empty') {
        if (result.event !== 'empty') {
          try {
            const eventResult = await api.handleEvent();
            set({ dungeon: { ...get().dungeon, playerHp: eventResult.playerHp } });
            // Return event info so BattleScene can show a brief notification
            return { event: result.event, eventResult, floor: result.currentFloor, autoAdvance: true };
          } catch (e) {
            console.error('[continueToNextFloor] handleEvent failed:', e);
          }
        }
        // For empty or after event handling, auto-advance to next floor
        // Release lock before recursive call so it can re-acquire
        set({ _floorTransitionInProgress: false });
        return get().continueToNextFloor();
      }

      return { event: result.event, ...result };
    } catch (err) {
      get().showToast(err.message);
      return { event: 'error', error: err.message };
    } finally {
      set({ _floorTransitionInProgress: false });
    }
  },

  handleEvent: async () => {
    try {
      const result = await api.handleEvent();
      set({ dungeon: { ...get().dungeon, playerHp: result.playerHp } });
      get().showToast(result.message);
      return result;
    } catch (err) {
      get().showToast(err.message);
      return null;
    }
  },

  fleeDungeon: async () => {
    try {
      const result = await api.fleeDungeon();
      set({ dungeon: null });
      get().showToast(result.message);
      return result;
    } catch (err) {
      get().showToast(err.message);
      return null;
    }
  },
}));

// 로컬 전투 상태를 UI용 battle 객체로 변환
function convertLocalToBattle(localState, initData) {
  // initData가 기존 battle 객체일 수 있음 (playLocalCard에서 호출 시)
  const playerName = initData?.party?.[0]?.name || initData?.player?.name || t('ui.player');

  return {
    battleId: initData?.battleId || 0,
    turn: localState.turn,
    ap: localState.ap,
    maxAp: localState.maxAp,
    status: localState.status,
    hand: localState.hand,
    deckCount: (localState.drawPile || localState.deck || []).length,
    discardCount: (localState.discardPile || localState.discard || []).length,
    exhaustCount: (localState.exhaustPile || localState.exhaust || []).length,
    playerBuffs: {
      strength: (localState.player.strength || 0) + (localState.player.tempStrength || 0),
      block: localState.player.block || 0,
      weak: localState.player.weak || (localState.player.debuffs?.weak) || 0,
      vulnerable: localState.player.vulnerable || (localState.player.debuffs?.vulnerable) || 0,
      poison: localState.player.poison || (localState.player.debuffs?.poison) || 0,
      burn: localState.player.burn || (localState.player.debuffs?.burn) || 0,
      frail: localState.player.frail || (localState.player.debuffs?.frail) || 0,
      dexterity: localState.player.dexterity || (localState.player.buffs?.dexterity) || 0,
      regen: localState.player.regen || (localState.player.buffs?.regen) || 0,
    },
    monsters: localState.monsters.map(m => ({
      id: m.id,
      name: m.name,
      emoji: m.emoji,
      image_url: m.image_url,
      hp: m.hp,
      maxHp: m.maxHp,
      intent: m.intent,
      buffs: m.buffs,
    })),
    party: [{
      type: 'player',
      id: 'player',
      name: playerName,
      classId: initData?.player?.classId || initData?.party?.[0]?.classId || 'warrior',
      hp: localState.player.hp,
      maxHp: localState.player.maxHp,
    }, ...(localState.mercenaries || []).map(m => ({
      type: 'mercenary',
      id: m.id,
      mercId: m.mercId, // 원본 용병 ID
      name: m.name,
      hp: m.hp,
      maxHp: m.maxHp,
      atk: m.atk,
      def: m.def,
      classId: m.classId
    }))],
    isLocal: true, // 로컬 전투 플래그
  };
}
